/*

  Util.ts

	Utility functions for the Explo/Netskrafl user interface

  Copyright (C) 2025 Miðeind ehf.
  Author: Vilhjálmur Þorsteinsson

  The Creative Commons Attribution-NonCommercial 4.0
  International Public License (CC-BY-NC 4.0) applies to this software.
  For further information, see https://github.com/mideind/Netskrafl

  The following code is based on
  https://developer.mozilla.org/en-US/docs/Web/API/Pointer_events/Pinch_zoom_gestures

*/

import { m, MithrilDragEvent, Vnode } from "./mithril";
import { BOARD_SIZE, ROWIDS } from "./types";
import { GlobalState } from "./globalstate";
import { getAudioManager, SoundId } from "./audio";

type UrlParams = "uuid" | "move" | "tab" | "faq" | "zombie";

// Possible URL parameters that are passed to routes
export type Params = Partial<Record<UrlParams, string>>;

type ZoomFunc = () => void;

// Global vars to cache event state
var evCache: PointerEvent[] = [];
var origDistance = -1;
const PINCH_THRESHOLD = 10; // Minimum pinch movement
var hasZoomed = false;

// Old-style (non-single-page) game URL prefix
const BOARD_PREFIX = "/board?game=";
const BOARD_PREFIX_LEN = BOARD_PREFIX.length;

export function addPinchZoom(attrs: any, funcZoomIn: ZoomFunc, funcZoomOut: ZoomFunc) {
  // Install event handlers for the pointer target
  attrs.onpointerdown = pointerdown_handler;
  attrs.onpointermove = pointermove_handler.bind(null, funcZoomIn, funcZoomOut);
  // Use same handler for pointer{up,cancel,out,leave} events since
  // the semantics for these events - in this app - are the same.
  attrs.onpointerup = pointerup_handler;
  attrs.onpointercancel = pointerup_handler;
  attrs.onpointerout = pointerup_handler;
  attrs.onpointerleave = pointerup_handler;
}

function pointerdown_handler(ev: PointerEvent) {
  // The pointerdown event signals the start of a touch interaction.
  // This event is cached to support 2-finger gestures
  evCache.push(ev);
}

function pointermove_handler(funcZoomIn: ZoomFunc, funcZoomOut: ZoomFunc, ev: PointerEvent) {
  // This function implements a 2-pointer horizontal pinch/zoom gesture. 
  //
  // If the distance between the two pointers has increased (zoom in), 
  // the target element's background is changed to "pink" and if the 
  // distance is decreasing (zoom out), the color is changed to "lightblue".
  //
  // Find this event in the cache and update its record with this event
  for (let i = 0; i < evCache.length; i++) {
    if (ev.pointerId === evCache[i].pointerId) {
      evCache[i] = ev;
      break;
    }
  }
 
  // If two pointers are down, check for pinch gestures
  if (evCache.length == 2) {
    // Calculate the distance between the two pointers
    const curDistance = Math.sqrt(
      Math.pow(evCache[0].clientX - evCache[1].clientX, 2) +
      Math.pow(evCache[0].clientY - evCache[1].clientY, 2)
    );
 
    if (origDistance > 0) {
      if (curDistance - origDistance >= PINCH_THRESHOLD) {
        // The distance between the two pointers has increased
        if (!hasZoomed)
          funcZoomIn();
        hasZoomed = true;
      }
      else
      if (origDistance - curDistance >= PINCH_THRESHOLD) {
        // The distance between the two pointers has decreased
        if (!hasZoomed)
          funcZoomOut();
        hasZoomed = true;
      }
    }
    else
    if (origDistance < 0) {
      // Note the original difference between two pointers
      origDistance = curDistance;
      hasZoomed = false;
    }
  }
}

function pointerup_handler(ev: PointerEvent) {
  // Remove this pointer from the cache and reset the target's
  // background and border
  remove_event(ev);
  // If the number of pointers down is less than two then reset diff tracker
  if (evCache.length < 2) {
    origDistance = -1;
  }
}

function remove_event(ev: PointerEvent) {
  // Remove this event from the target's cache
  for (let i = 0; i < evCache.length; i++) {
    if (evCache[i].pointerId === ev.pointerId) {
      evCache.splice(i, 1);
      break;
    }
  }
}

export function buttonOver(ev: Event) {
  const clist = (ev.currentTarget as HTMLElement).classList;
  if (clist !== undefined && !clist.contains("disabled"))
    clist.add("over");
  (ev as MithrilDragEvent).redraw = false;
}

export function buttonOut(ev: Event) {
  const clist = (ev.currentTarget as HTMLElement).classList;
  if (clist !== undefined)
    clist.remove("over");
  (ev as MithrilDragEvent).redraw = false;
}

// Glyphicon utility function: inserts a glyphicon span
export function glyph(icon: string, attrs?: any, grayed?: boolean): Vnode {
  return m("span.glyphicon.glyphicon-" + icon + (grayed ? ".grayed" : ""), attrs);
}

export function glyphGrayed(icon: string, attrs?: any): Vnode {
  return m("span.glyphicon.glyphicon-" + icon + ".grayed", attrs);
}

// Utility function: inserts non-breaking space
export function nbsp(n?: number): Vnode {
  if (!n || n === 1)
    return m.trust("&nbsp;");
  let r: Vnode[] = [];
  for (let i = 0; i < n; i++)
    r.push(m.trust("&nbsp;"));
  return m.fragment({}, r);
}

// Utility functions

export function escapeHtml(string: string): string {
  /* Utility function to properly encode a string into HTML */
  const entityMap: Record<string, string> = {
    "&": "&amp;",
    "<": "&lt;",
    ">": "&gt;",
    '"': '&quot;',
    "'": '&#39;',
    "/": '&#x2F;'
  };
  return String(string).replace(/[&<>"'/]/g, (s) => entityMap[s] ?? "");
}

export function getUrlVars(url: string): Params {
  // Get values from a URL query string
  const hashes = url.split('&');
  const vars: Params = {};
  for (let i = 0; i < hashes.length; i++) {
    const hash = hashes[i].split('=');
    if (hash.length == 2)
      vars[hash[0] as keyof Params] = decodeURIComponent(hash[1]);
  }
  return vars;
}

export function getInput(id: string): string {
  // Return the current value of a text input field
  const elem = document.getElementById(id) as HTMLInputElement;
  return elem.value;
}

export function setInput(id: string, val: string) {
  // Set the current value of a text input field
  const elem = document.getElementById(id) as HTMLInputElement;
  elem.value = val;
}

export function playAudio(state: GlobalState, soundId: string) {
  // Play an audio file using the AudioManager
  const audioManager = getAudioManager(state);
  audioManager.play(soundId as SoundId);
}

export function arrayEqual(a: any[], b: any[]): boolean {
  // Return true if arrays a and b are equal
  if (a.length != b.length)
    return false;
  for (let i = 0; i < a.length; i++)
    if (a[i] != b[i])
      return false;
  return true;
}

export function gameUrl(url: string): string {
  // Convert old-style game URL to new-style single-page URL
  // The URL format is "/board?game=ed27b9f0-d429-11eb-8bc7-d43d7ee303b2&zombie=1"
  if (url.slice(0, BOARD_PREFIX_LEN) == BOARD_PREFIX)
    // Cut off "/board?game="
    url = url.slice(BOARD_PREFIX_LEN);
  // Isolate the game UUID
  const uuid = url.slice(0, 36);
  // Isolate the other parameters, if any
  let params = url.slice(36);
  // Start parameter section of URL with a ? sign
  if (params.length > 0 && params.charAt(0) == "&")
    params = "?" + params.slice(1);
  // Return the single-page URL, to be consumed by m.route.Link()
  return "/game/" + uuid + params;
}

export function scrollMovelistToBottom(): void {
  // If the length of the move list has changed,
  // scroll the last move into view
  let movelist = document.querySelectorAll("div.movelist .move");
  if (!movelist || !movelist.length)
    return;
  let target = movelist[movelist.length - 1] as HTMLElement;
  let parent = target.parentNode as HTMLElement;
  let len = parent.getAttribute("data-len");
  let intLen = (!len) ? 0 : parseInt(len);
  if (movelist.length > intLen) {
    // The list has grown since we last updated it:
    // scroll to the bottom and mark its length
    parent.scrollTop = target.offsetTop;
  }
  parent.setAttribute("data-len", movelist.length.toString());
}

export function coord(row: number, col: number, vertical: boolean = false): string | null {
  // Return the co-ordinate string for the given 0-based row and col
  if (row < 0 || row >= BOARD_SIZE || col < 0 || col >= BOARD_SIZE)
    return null;
  // Horizontal moves have the row letter first
  // Vertical moves have the column number first
  return vertical ? `${col + 1}${ROWIDS[row]}` : `${ROWIDS[row]}${col + 1}`;
}

export function toVector(co: string): { col: number, row: number, dx: number, dy: number } {
  // Convert a co-ordinate string to a 0-based row, col and direction vector
  var dx = 0, dy = 0;
  var col = 0;
  var row = ROWIDS.indexOf(co[0]);
  if (row >= 0) {
    /* Horizontal move */
    col = parseInt(co.slice(1)) - 1;
    dx = 1;
  }
  else {
    /* Vertical move */
    row = ROWIDS.indexOf(co.slice(-1));
    col = parseInt(co) - 1;
    dy = 1;
  }
  return { col: col, row: row, dx: dx, dy: dy };
}

export function valueOrK(value: number, breakpoint: number = 10000): string {
  // Return a numeric value as a string, but in kilos (thousands)
  // if it exceeds a breakpoint, in that case suffixed by "K"
  const sign = value < 0 ? "-" : "";
  value = Math.abs(value);
  if (value < breakpoint)
    return `${sign}${value}`;
  value = Math.round(value / 1000);
  return `${sign}${value}K`;
}
